import { makeAutocompleteServerSource } from "./autocompleteApi.js";

const DEBOUNCE_MS = 250;

export function initAutocomplete() {
  const autocompleteContainers = document.querySelectorAll(".js-autocomplete");
  if (!autocompleteContainers.length) return;

  autocompleteContainers.forEach((container) => {
    // prevent double initialization
    if (container.dataset.acInit === "true") return;
    container.dataset.acInit = "true";

    const input = container.querySelector('input[role="combobox"]');
    const menu = container.querySelector(".autocomplete__menu");
    const hidden = document.createElement("input");

    const name = container.dataset.name || "field";
    const sourceKey = container.dataset.key || name;

    hidden.type = "hidden";
    hidden.name = name;
    container.appendChild(hidden);

    // Choose the source function based on data-key (e.g. "address" or "city")
    const serverSource = makeAutocompleteServerSource();
    const getSourceFn = () => serverSource;

    let items = [];
    let highlight = -1;

    // Ensures the highlighted option is visible in the scrollable menu
    // (called after changing highlight)
    function ensureVisible(idx) {
      const opt = menu.querySelector(
        `.autocomplete__option[data-idx="${idx}"]`
      );
      if (!opt) return;
      const optTop = opt.offsetTop;
      const optBottom = optTop + opt.offsetHeight;
      const viewTop = menu.scrollTop;
      const viewBottom = viewTop + menu.clientHeight;

      if (optTop < viewTop) {
        menu.scrollTop = optTop - 4; // padding
      } else if (optBottom > viewBottom) {
        menu.scrollTop = optBottom - menu.clientHeight + 4;
      }
    }

    // Changing the active (highlighted) option
    function setActive(idx) {
      highlight = idx;
      const options = menu.querySelectorAll(".autocomplete__option");
      options.forEach((el, i) => {
        const isActive = i === highlight;
        el.classList.toggle("is-active", isActive);
        el.setAttribute("aria-selected", String(isActive));
        if (isActive && !el.id) {
          el.id = `${name}-opt-${i}`;
        }
      });
      ensureVisible(highlight);
      const activeEl = menu.querySelector(
        `.autocomplete__option[data-idx="${highlight}"]`
      );
      if (activeEl) {
        if (!menu.id) {
          menu.id = `${name}-listbox-${Math.random().toString(36).slice(2)}`;
          input.setAttribute("aria-controls", menu.id);
        }
        input.setAttribute("aria-activedescendant", activeEl.id);
      } else {
        input.removeAttribute("aria-activedescendant");
      }
    }

    function render() {
      if (!items.length) {
        menu.innerHTML = `<li class="autocomplete__empty">No results</li>`;
        return;
      }
      menu.innerHTML = items
        .map(
          (it, idx) => `
            <li class="autocomplete__option ${idx === highlight ? "is-active" : ""}"
                data-idx="${idx}" role="option" aria-selected="${idx === highlight}">
              <div class="autocomplete__line1">${escapeHtml(it.label)}</div>
            </li>`
        )
        .join("");

      // listeners for options
      menu.querySelectorAll(".autocomplete__option").forEach((li) => {
        li.addEventListener("mouseenter", () =>
          setActive(parseInt(li.dataset.idx, 10))
        );
        li.addEventListener("mousedown", (e) => {
          e.preventDefault();
          const idx = parseInt(li.dataset.idx, 10);
          commit(items[idx]);
        });
      });

      if (!menu.id) {
        menu.id = `${name}-listbox-${Math.random().toString(36).slice(2)}`;
        input.setAttribute("aria-controls", menu.id);
      }
      if (highlight >= 0) ensureVisible(highlight);
    }

    function commit(item) {
      input.value = item.label;
      hidden.value = item.label;

      const form = container.closest("form");

      // Allow customization of target selectors via data attributes
      const citySelector = container.dataset.cityTarget || 'input[name="city"]';
      const stateSelector =
        container.dataset.stateTarget || 'select[name="state-select"]';

      // CITY
      if (form && item.city) {
        const cityInput = form.querySelector(citySelector);
        if (cityInput) {
          cityInput.value = item.city;
          cityInput.dispatchEvent(new Event("input", { bubbles: true }));
          cityInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      // STATE
      if (form && item.state) {
        const stateSelect = form.querySelector(stateSelector);
        if (stateSelect) setSelectValueByValueOrText(stateSelect, item.state);
      }

      container.dispatchEvent(
        new CustomEvent("autocomplete:select", {
          bubbles: true,
          detail: { item },
        })
      );

      close();
    }

    function setSelectValueByValueOrText(selectEl, wanted) {
      const t = String(wanted).trim().toLowerCase();
      const opts = Array.from(selectEl.options);
      let match =
        opts.find((o) => String(o.value).toLowerCase() === t) ||
        opts.find((o) => String(o.text).toLowerCase() === t);

      if (match) {
        selectEl.value = match.value;
        selectEl.dispatchEvent(new Event("input", { bubbles: true }));
        selectEl.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    function open() {
      if (items.length) {
        menu.hidden = false;
        input.setAttribute("aria-expanded", "true");
        if (highlight >= 0) ensureVisible(highlight);
      }
    }

    function close() {
      menu.hidden = true;
      input.setAttribute("aria-expanded", "false");
      highlight = -1;
      input.removeAttribute("aria-activedescendant");
    }

    async function handleInput(e) {
      const q = e.target.value.trim();
      hidden.value = q;

      if (q.length < 1) {
        items = [];
        render();
        close();
        return;
      }

      const sourceFn = getSourceFn();
      const res = await sourceFn(q);
      items = Array.isArray(res) ? res : [];
      highlight = items.length ? 0 : -1;
      menu.scrollTop = 0; // Resets scroll position
      render();
      if (highlight >= 0) setActive(highlight);
      open();
    }

    // Handle input with debounce
    const handleInputDebounced = debounce(handleInput, DEBOUNCE_MS);

    function handleKey(e) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (items.length) setActive((highlight + 1) % items.length);
          break;
        case "ArrowUp":
          e.preventDefault();
          if (items.length)
            setActive((highlight - 1 + items.length) % items.length);
          break;
        case "Enter":
          if (!menu.hidden && highlight >= 0) {
            e.preventDefault();
            commit(items[highlight]);
          }
          break;
        case "Escape":
          close();
          break;
      }
    }

    // Listeners
    input.addEventListener("input", handleInputDebounced);
    input.addEventListener("keydown", handleKey);
    input.addEventListener("focus", () => {
      if (items.length) open();
    });
    document.addEventListener("mousedown", (e) => {
      if (!container.contains(e.target)) close();
    });

    // Initial state
    menu.hidden = true;
    hidden.value = input.value;
  });
}

/* Utils */
function debounce(fn, ms = 150) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}
function escapeHtml(s) {
  return String(s).replace(
    /[&<>"']/g,
    (m) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[m]
  );
}
